/**
 * "From this site" — the client's OWN photographs, browsable in the picker.
 *
 * ## Why this source exists
 *
 * Measured across 50 live client sites and 50 template bundles (2,500 skins),
 * the skin pipeline placed a median of **one** distinct client photograph into
 * a median of **26** photo slots, and on 23.6% of skins it placed none at all.
 * 52.4% of every photo slot in the corpus kept the TEMPLATE business's image.
 *
 * The images were never missing. `extractHeroImages` read `img[src]` inside ONE
 * element of ONE page and returned 1.3% of what the scrape had already
 * downloaded — a median client site carries 81 photographs. SU-1 collects them
 * all into `profile.siteImages`; this is the surface that lets a person use
 * them.
 *
 * That division of labour is the point. Choosing WHICH of a hundred photographs
 * belongs in "our team" versus "recent work" is not something a deterministic
 * pipeline can do, and guessing is what produced the defect. So the skinner
 * takes the slots it is confident about and this source takes the rest, one
 * click each.
 *
 * ## No `getSourceUrl`, deliberately
 *
 * `image-picker/session.js`'s CORS fallback calls that hook and applies the RAW
 * url — in its own words, it "skips the `pb-asset://` pipeline entirely". These
 * are third-party URLs on the client's own server. A raw one surviving into a
 * saved project or an export is a hotlink to somebody else's origin that breaks
 * the day they redeploy, which is the asset-rot problem this initiative is
 * trying to REMOVE. The LogoNuri adapter omits the hook for the same reason and
 * a different threat.
 *
 * ## Provenance is legitimate here, and that is NOT the LogoNuri rule
 *
 * LogoNuri must sanitise `source` because its URLs are authenticated per-user
 * paths that must never reach `assets-meta.json` in an export. Here the URL is
 * the client's own public site — correct, stable, useful provenance. The rule
 * transfers as "be deliberate about what `meta.source` says", not as "hide it".
 */

import { externalImageTransports, fetchAssetThroughCache } from '../../export/asset-resolver.js';

/**
 * The active project's inventory, injected rather than read.
 *
 * `editing/adapters/` does not import the store — no adapter does — and the
 * one existing precedent for per-session adapter state is LogoNuri's
 * `setKitAssetPurpose`. Injection also makes the source testable without a
 * store, and makes "this project has no inventory" a state the caller states
 * rather than one this module infers.
 */
let inventory = [];

/** @param {Array<object>} next — `profile.siteImages` from the composition context. */
export function setSiteImageInventory(next) {
  inventory = Array.isArray(next) ? next.filter((entry) => entry && entry.url) : [];
}

export function resetSiteImageInventory() {
  inventory = [];
}

/** How many images this project can offer. `visibleSources()` gates on it. */
export function siteImageCount() {
  return inventory.length;
}

function normalizeUrl(rawUrl) {
  const value = String(rawUrl || '').trim();
  if (!value) return '';
  try {
    const url = new URL(value);
    url.hash = '';
    return url.href;
  } catch {
    return value;
  }
}

function basenameFromUrl(rawUrl) {
  const normalized = normalizeUrl(rawUrl);
  if (!normalized) return '';
  try {
    const path = new URL(normalized).pathname;
    return decodeURIComponent(path.split('/').filter(Boolean).pop() || '');
  } catch {
    return '';
  }
}

/**
 * An adapter result.
 *
 * Note what this does NOT compute: the facts line and the media kind. The card
 * model derives both from `_raw.filename` / `_raw.width` / `_raw.height` via
 * `metaLineFor` and `classifyMedia`, so computing them here would be a second
 * answer to a settled question — and the fit rule (photographs crop, graphics
 * are shown whole on a checkerboard and never upscaled) is exactly the kind of
 * decision that must not have two implementations.
 */
function toResult(entry, index) {
  return {
    id: `site-${index}-${normalizeUrl(entry.url)}`,
    thumb: entry.url,
    title: entry.alt?.trim() || basenameFromUrl(entry.url) || 'Image',
    // The PAGE it was found on is the useful provenance, and it is what
    // discriminates between two otherwise identical cards.
    author: entry.pageTitle || '',
    authorUrl: entry.pageUrl || '',
    variantCount: (entry.variants?.length || 0) + 1,
    variants: (entry.variants || []).map((url) => ({ url, _normalizedUrl: normalizeUrl(url) })),
    dead: false,
    _raw: {
      ...entry,
      _normalizedUrl: normalizeUrl(entry.url),
      filename: basenameFromUrl(entry.url)
    }
  };
}

const PAGE_SIZE = 60;

export const siteImages = {
  name: 'From this site',
  needsKey: false,
  lsKey: null,
  attribution: "Images found on the client's own website",
  keyHelp: '',

  browseWithoutQuery: true,
  browseHint: 'Browse the images found on the site you skinned from, or search by name.',

  getApiKey() {
    return '';
  },
  setApiKey() {},

  // NO getSourceUrl — see the module docstring. Its absence is the feature.

  normalizeUrl,
  basenameFromUrl,

  /** Categories are the ROLES the extractor assigned, so a photo hunt skips icons. */
  getCategories() {
    const counts = new Map();
    for (const entry of inventory) {
      const role = entry.role || 'photo';
      counts.set(role, (counts.get(role) || 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([role, n]) => ({ value: role, label: `${role} (${n})` }));
  },

  async findByUrl(rawUrl) {
    const target = normalizeUrl(rawUrl);
    if (!target) return null;
    const index = inventory.findIndex(
      (entry) =>
        normalizeUrl(entry.url) === target ||
        (entry.variants || []).some((v) => normalizeUrl(v) === target)
    );
    return index === -1 ? null : toResult(inventory[index], index);
  },

  async search(query, page = 1, _signal, { category = '' } = {}) {
    let rows = inventory.map((entry, index) => ({ entry, index }));
    if (category) rows = rows.filter(({ entry }) => (entry.role || 'photo') === category);
    const q = String(query || '')
      .trim()
      .toLowerCase();
    if (q) {
      rows = rows.filter(({ entry }) =>
        `${entry.alt || ''} ${entry.pageTitle || ''} ${basenameFromUrl(entry.url)}`
          .toLowerCase()
          .includes(q)
      );
    }
    const start = (Math.max(1, page) - 1) * PAGE_SIZE;
    return {
      results: rows
        .slice(start, start + PAGE_SIZE)
        .map(({ entry, index }) => toResult(entry, index)),
      total: rows.length
    };
  },

  /**
   * Fetch the bytes.
   *
   * Through the SAME transport chain `adoptAssets` uses — the server-side
   * `/api/export/assets/fetch` proxy first, then a direct fetch. That ordering
   * is load-bearing here rather than an optimisation: these URLs are on the
   * CLIENT's own web server, which has no reason to send
   * `Access-Control-Allow-Origin` to the builder, so a browser-only fetch would
   * fail CORS on most real sites. The proxy is the normal path and the direct
   * fetch is the fallback, not the other way round.
   *
   * `adapter.getSourceUrl` is deliberately absent (see the module docstring),
   * so the session's raw-URL CORS escape hatch cannot fire. If both transports
   * fail, the pick fails and says so — which is correct. Silently applying the
   * raw URL would put a hotlink to the client's origin into their project.
   */
  async download(item) {
    const url = item?._raw?._normalizedUrl || normalizeUrl(item?._raw?.url);
    if (!url) {
      throw Object.assign(new Error('This image URL is invalid. Try a different image.'), {
        code: 'INVALID_URL'
      });
    }
    const out = await fetchAssetThroughCache(url, externalImageTransports(url), {}, {});
    if (!out?.ok || !out.blob?.size) {
      throw Object.assign(
        new Error('That image could not be downloaded from the site. Try another one.'),
        { code: 'DOWNLOAD_FAILED' }
      );
    }
    return new File([out.blob], item._raw.filename || 'image.jpg', {
      type: out.blob.type || 'image/jpeg'
    });
  }
};
